import { subscribe, TConnectionItem } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { addDays, subDays } from 'date-fns';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { client } from '../../config/api';
import { onSubscriptionData } from '../../config/cache';
import {
  ListOneShipmentDocument,
  OnAnyEventGiftCreatedDocument,
  OnAnyShipmentChangedDocument,
  ShipmentFieldsFragmentDoc,
  TGetShipmentQuery,
  TGetShipmentQueryVariables,
  TListOneShipmentQuery,
  TListOneShipmentQueryVariables,
  TListOpenShipmentsNewQuery,
  TListOpenShipmentsNewQueryVariables,
  TListSetForPickupShipmentsQuery,
  TListSetForPickupShipmentsQueryVariables,
  TListShipmentsByAddressAndStatusQuery,
  TListShipmentsByAddressAndStatusQueryVariables,
  TListShipmentsPickupNewQuery,
  TListShipmentsPickupNewQueryVariables,
  TListShipmentsTrackingNewQuery,
  TListShipmentsTrackingNewQueryVariables,
  TListShippedShipmentsQuery,
  TListShippedShipmentsQueryVariables,
  TOnAnyShipmentChangedSubscription,
  TOnAnyShipmentChangedSubscriptionVariables,
  TShipment,
  TShipmentFieldsFragment,
  TShipmentNewFieldsFragment,
  TShipmentStatuses,
  useGetShipmentLazyQuery,
  useListOneShipmentLazyQuery,
  useListOpenShipmentsNewLazyQuery,
  useListSetForPickupShipmentsLazyQuery,
  useListShipmentsByAddressAndStatusLazyQuery,
  useListShipmentsPickupNewLazyQuery,
  useListShipmentsTrackingNewLazyQuery,
  useListShippedShipmentsLazyQuery
} from '../../generated/graphql';
import { TAddressType } from '../addresses/queries';
import { TDependantType } from '../dependants/queries';
import { TEmployeeType } from '../employees/queries';
import { TGiftType } from '../gifts/queries';
import { TOfficeType } from '../office/queries';
import { useFragmentOrFetch } from '../shared/useFragmentOrFetch';
import { useQueryAll } from '../shared/useQueryAll';
import { TShipmentAddressInfo } from './shared';
import { shipmentsGenerateFilters } from './utils';

const onShipmentSubscriptionData = async (data?: TOnAnyShipmentChangedSubscription, vars?: TOnAnyShipmentChangedSubscriptionVariables) => {
  const { companyId, id, _deleted } = data?.onAnyShipmentChanged || {};

  if (_deleted)
    return onSubscriptionData(
      {
        onAnyShipmentChanged: {
          ...(data?.onAnyShipmentChanged || {}),
          address: null,
          gifts: null
        }
      } as Record<string, any>,
      vars
    );
  if (!companyId || !id) return;

  const response = await client.query<TListOneShipmentQuery, TListOneShipmentQueryVariables>({
    query: ListOneShipmentDocument,
    variables: {
      id
    }
  });

  const shipmentData = response.data.listOneShipment.items[0] as TConnectionItem | undefined;

  if (shipmentData) {
    const subscriptionData = {
      onAnyShipmentChanged: shipmentData
    };

    onSubscriptionData(subscriptionData, vars);
  }
};

let anyShipmetnChangedSubscribed = false;
let anyGiftRemovedFromGroupSubscribed = false;

export const useSubscribeToShipmentChanged = () => {
  useEffect(() => {
    if (!anyShipmetnChangedSubscribed) {
      subscribe({
        client,
        query: OnAnyShipmentChangedDocument,
        onSubscriptionData: (data, vars) => {
          return onShipmentSubscriptionData(data as TOnAnyShipmentChangedSubscription, vars as TOnAnyShipmentChangedSubscriptionVariables);
        }
      });

      anyShipmetnChangedSubscribed = true;
    }
  }, []);

  useEffect(() => {
    if (!anyGiftRemovedFromGroupSubscribed) {
      subscribe({
        client,
        query: OnAnyEventGiftCreatedDocument,
        onSubscriptionData: async (data, vars) => {
          const { event } = data?.onAnyGiftRemovedFromGroup || {};

          await onShipmentSubscriptionData(
            {
              onAnyShipmentChanged: event
            },
            vars as TOnAnyShipmentChangedSubscriptionVariables
          );
        }
      });

      anyGiftRemovedFromGroupSubscribed = true;
    }
  }, []);
};

// LIST

export const useQueryAllOpenShipments = () => {
  useSubscribeToShipmentChanged();

  const { data, error, loading } = useQueryAll<TListOpenShipmentsNewQuery, TListOpenShipmentsNewQueryVariables>({
    useQuery: useListOpenShipmentsNewLazyQuery
  });

  const shipments = useMemo(() => _.compact(data?.listOpenShipmentsNEW?.items) as unknown as TShipmentType[], [data]);

  return {
    data: shipments,
    error: error,
    loading: loading
  };
};

export const useQueryAllSetForPickupShipments = () => {
  useSubscribeToShipmentChanged();

  const { data, error, loading } = useQueryAll<TListSetForPickupShipmentsQuery, TListSetForPickupShipmentsQueryVariables>({
    useQuery: useListSetForPickupShipmentsLazyQuery
  });

  const shipments = useMemo(() => _.compact(data?.listSetForPickupShipments?.items) as TShipmentType[], [data]);

  return {
    data: shipments,
    error: error,
    loading: loading
  };
};

export const useQueryAllShippedShipments = () => {
  useSubscribeToShipmentChanged();

  const { data, error, loading } = useQueryAll<TListShippedShipmentsQuery, TListShippedShipmentsQueryVariables>({
    useQuery: useListShippedShipmentsLazyQuery
  });

  const shipments = useMemo(() => _.compact(data?.listShippedShipments?.items) as TShipmentType[], [data]);

  return {
    data: shipments,
    error: error,
    loading: loading
  };
};

export const useQueryAllShipmentFromAddressWithoutALabel = (props: { addressId: string }) => {
  useSubscribeToShipmentChanged();

  const { addressId } = props;

  const {
    data: packagingData,
    error: packagingError,
    loading: packagingLoading
  } = useQueryAll<TListShipmentsByAddressAndStatusQuery, TListShipmentsByAddressAndStatusQueryVariables>({
    useQuery: useListShipmentsByAddressAndStatusLazyQuery,
    variables: {
      addressId,
      status: TShipmentStatuses.Packaging
    }
  });

  const {
    data: readyToShipData,
    error: readyToShipError,
    loading: readyToShipLoading
  } = useQueryAll<TListShipmentsByAddressAndStatusQuery, TListShipmentsByAddressAndStatusQueryVariables>({
    useQuery: useListShipmentsByAddressAndStatusLazyQuery,
    variables: {
      addressId,
      status: TShipmentStatuses.ReadyToShip
    }
  });

  const fullShipments = useMemo(() => _.compact(packagingData?.listShipmentsByAddressAndStatus.items).concat(_.compact(readyToShipData?.listShipmentsByAddressAndStatus.items)) as TShipmentType[], [packagingData, readyToShipData]);

  return {
    data: fullShipments,
    error: packagingError || readyToShipError,
    loading: packagingLoading || readyToShipLoading
  };
};

// ITEM

export const useFragmentOrFetchShipment = (variables: TGetShipmentQueryVariables) => {
  useSubscribeToShipmentChanged();

  const { data, error, loading } = useFragmentOrFetch<TShipmentFieldsFragment, TGetShipmentQuery, TGetShipmentQueryVariables>({
    useLazyQuery: useGetShipmentLazyQuery,
    variables,
    fragmentDoc: ShipmentFieldsFragmentDoc,
    __typename: 'Shipment'
  });

  const shipments = useMemo(() => _.compact([data]) as TShipmentFieldsFragment[], [data]);

  return {
    data: shipments[0] as TShipmentType,
    error: error,
    loading: loading
  };
};

// =========================
// NEW
// =========================

export type TUseShipmentsFilter = {
  includingNextDays?: number;
  includingLastDays?: number;
};

export type TShipmentType = TShipmentNewFieldsFragment &
  TShipmentAddressInfo & {
    company?: Record<string, any>;
    office?: TOfficeType;
    employee?: TEmployeeType;
    address?: TAddressType;
    recipient?: TEmployeeType | TDependantType;
    gifts?: { items?: Array<TGiftType> } | Array<TGiftType>;
    groupable?: boolean;
    groupableItems?: TShipment[];
  };

export const useQueryAllOpenShipmentsNEW = () => {
  useSubscribeToShipmentChanged();

  const { data, error, loading } = useQueryAll<TListOpenShipmentsNewQuery, TListOpenShipmentsNewQueryVariables>({
    useQuery: useListOpenShipmentsNewLazyQuery
  });

  const shipments = useMemo(() => data?.listOpenShipmentsNEW.items || [], [data]);

  return {
    data: shipments as unknown as TShipmentType[],
    error,
    loading
  };
};

export const useQueryAllOpenShipmentsTrakingNEW = (props: TUseShipmentsFilter) => {
  useSubscribeToShipmentChanged();

  const shippedAt = useMemo(
    () =>
      props.includingNextDays && props.includingNextDays != 999
        ? JSON.stringify({
            operator: 'BETWEEN',
            value: [subDays(new Date(), props.includingNextDays).toISOString(), addDays(new Date(), props.includingNextDays).toISOString()]
          })
        : null,
    [props.includingNextDays]
  );

  const filter = shipmentsGenerateFilters(props);

  const { data, error, loading } = useQueryAll<TListShipmentsTrackingNewQuery, TListShipmentsTrackingNewQueryVariables>({
    useQuery: useListShipmentsTrackingNewLazyQuery,
    variables: {
      shippedAt,
      filter,
      limit: 500
    }
  });

  const shipments = useMemo(() => data?.listShipmentsTrackingNEW.items || [], [data]);

  return {
    data: shipments as unknown as TShipmentType[],
    error,
    loading
  };
};

export const useQueryAllOpenShipmentsPickupNEW = (props: TUseShipmentsFilter) => {
  useSubscribeToShipmentChanged();

  const setForPickupAt = useMemo(
    () =>
      props.includingNextDays && props.includingNextDays != 999
        ? JSON.stringify({
            operator: 'BETWEEN',
            value: [subDays(new Date(), props.includingNextDays).toISOString(), addDays(new Date(), props.includingNextDays).toISOString()]
          })
        : null,
    [props.includingNextDays]
  );

  const filter = shipmentsGenerateFilters(props);

  const { data, error, loading } = useQueryAll<TListShipmentsPickupNewQuery, TListShipmentsPickupNewQueryVariables>({
    useQuery: useListShipmentsPickupNewLazyQuery,
    variables: {
      setForPickupAt,
      filter,
      limit: 100
    }
  });

  const shipments = useMemo(() => data?.listShipmentsPickupNEW?.items || [], [data]);

  return {
    data: shipments as unknown as TShipmentType[],
    error,
    loading
  };
};

export const useQueryOneShipment = (variables: TListOneShipmentQueryVariables) => {
  useSubscribeToShipmentChanged();

  const { data, error, loading, refetch } = useQueryAll<TListOneShipmentQuery, TListOneShipmentQueryVariables>({
    useQuery: useListOneShipmentLazyQuery,
    variables
  });

  const shipment = useMemo(() => data?.listOneShipment.items[0] || {}, [data]);

  return {
    data: shipment as TShipmentType,
    error,
    loading,
    refetch
  };
};
